import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, useParams, useSearchParams } from "@remix-run/react";
import { dehydrate, QueryClient, useQuery } from "@tanstack/react-query";
import { WorkspaceSearchResult } from "~/.server/api/routers/workspace";
import { trpcServer } from "~/common/trcp";
import { useCreatePageUrl } from "~/utils/hooks/useCreatePageUrl";
import WorkspaceListLayout from "~/components/workspaceListLayout";
import WorkspaceList from "~/components/workspaceList";
import { SortBy, SortOrder, SortValue } from "~/components/sorting";
import { MapBounds, deserializeBounds } from "~/utils/mapCache";
import { useCallback } from "react";

const PAGINATION_PAGE_SIZE = 100

export async function loader({ request, params }: LoaderFunctionArgs) { 
  const { city } = params;
  const url = new URL(request.url);
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const [sortBy, sortOrder] = (url.searchParams.get('sort') || 'createdAt-desc').split('-') as [SortBy, SortOrder];
  const boundsParam = url.searchParams.get('bounds');
  const bounds = deserializeBounds(boundsParam);

  const queryClient = new QueryClient()

  const isFilteredByCity = city !== 'all'

  const pageSize = isFilteredByCity ? PAGINATION_PAGE_SIZE : 20;
  const workspaceData = await trpcServer(request).workspace.getFilteredWorkspaces.query({
    pagination: { page, pageSize },
    sorting: { sortBy, sortOrder },
    filters: {
      ...(isFilteredByCity ? { citySlug: city } : {}),
      ...(bounds ? { bounds } : {})
    }
  });

  await queryClient.prefetchQuery({
    queryKey: [city, page, sortBy, sortOrder, boundsParam],
    queryFn: async () => workspaceData,
  });

  const totalPages = Math.ceil((workspaceData.pagination?.totalCount || 0) / pageSize);

  return json({
    dehydratedState: dehydrate(queryClient),
    page,
    totalPages,
    city,
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const city = data?.city;
  const page = data?.page || 1;
  const totalPages = data?.totalPages || 1;
  const isFilteredByCity = city !== 'all';

  const formattedCity = isFilteredByCity && city
    ? city.charAt(0).toUpperCase() + city.slice(1)
    : 'todas las ciudades';

  const title = `Espacios en ${formattedCity}${page > 1 ? ` - Página ${page}` : ''} | Compartir Espacios`;
  const description = `Encuentra oficinas, coworkings, talleres y espacios de trabajo en ${formattedCity}. Alquila el espacio perfecto para tu negocio.`;
  const canonicalUrl = `https://www.compartirespacios.com/espacios/${city}`;
  const pageUrl = page > 1 ? `${canonicalUrl}?page=${page}` : canonicalUrl;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:url", content: pageUrl },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: "es_ES" },
    { property: "og:site_name", content: "Compartir Espacios" },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { tagName: "link", rel: "canonical", href: page > 1 ? pageUrl : canonicalUrl },
    ...(page > 1 ? [{ tagName: "link", rel: "prev", href: page === 2 ? canonicalUrl : `${canonicalUrl}?page=${page - 1}` }] : []),
    ...(page < totalPages ? [{ tagName: "link", rel: "next", href: `${canonicalUrl}?page=${page + 1}` }] : []),
  ];
};

export default function City() {
  const { city } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sort = (searchParams.get('sort') || 'createdAt-desc') as SortValue;
  const boundsParam = searchParams.get('bounds');
  const createPageUrl = useCreatePageUrl();

  const handleSort = (value: SortValue) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    params.set('page', '1');
    setSearchParams(params);
  };
 
  const [sortBy, sortOrder] = sort.split('-') as [SortBy, SortOrder];
 
  const { data } = useQuery<WorkspaceSearchResult>({
    queryKey: [city, page, sortBy, sortOrder, boundsParam]
  });

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    // When bounds change, we'll refetch the data
    // The URL params are already updated in the map component
  }, []);

  const isFilteredByCity = city !== 'all'
  const formattedCityName = (isFilteredByCity && city) 
  ? city.charAt(0).toUpperCase() + city.slice(1)
  : 'todas las ciudades' 

  return (
    <div className="container mx-auto px-4">
      <WorkspaceListLayout 
        title={`Espacios en ${formattedCityName}`}
        showMap={isFilteredByCity}
        workspaces={data?.workspaces || []}
        totalCount={data?.pagination?.totalCount}
        searchWithinBounds={true}
        onBoundsChange={handleBoundsChange}
        sort={{
          value: sort,
          onValueChange: handleSort
        }}
      >
        <WorkspaceList 
          data={data} 
          showMap={isFilteredByCity}
          createPageUrl={createPageUrl}
          pageSize={PAGINATION_PAGE_SIZE}
        />
      </WorkspaceListLayout>
    </div>
  );
}
